import {
  type RecordStocktakeRequest,
  type ShelfContentsResponse,
  type ShelfWine,
  type StocktakeOutcome,
  type StocktakeResponse,
  type StorageSummary,
  type WineDetail,
} from "@corklog/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router";

import {
  ApiError,
  fetchShelfContents,
  fetchStorages,
  fetchWine,
  fetchWines,
  recordStocktake,
} from "../api.js";
import { useMayWrite } from "../cellar.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { OwnersOnly } from "../components/OwnersOnly.js";
import {
  actionButton,
  control,
  fieldLabel,
  narrowed,
  resting,
  Select,
} from "../components/controls.js";
import { useReload } from "../offline.js";

/**
 * Walking a shelf and writing down what is actually on it.
 *
 * Task `8.2`. `project-scope.md` puts stocktaking in the application to admit
 * that the record drifts from the cabinet rather than to pretend it does not:
 * over twenty years bottles get moved without being recorded, carried to
 * dinners they do not come back from, and given away in moments nobody logged.
 * An inventory that claims to be exact stops being trusted, and an inventory
 * nobody trusts ends up back in a spreadsheet.
 *
 * **The shelf is the unit, and it is in the address.** A walk is one person in
 * front of one cabinet door, so `?hylla=` carries it: a walk survives a
 * reload, and the shelf you are standing at is a link somebody can be sent.
 *
 * **Nothing is written until the walk is saved.** One request at the end,
 * because a walk is one session and the record of it should be one row or
 * none — and because holding it in the browser is what lets the walk happen in
 * a basement with no signal. A refused save keeps every tick, exactly as the
 * walk through the cabinets at `8.22` keeps what was typed.
 *
 * **A row left untouched is a result.** Not confirming is what the record says
 * when nobody knows: the bottle stays where it was with the `lastConfirmedAt`
 * it already had, and that date going stale is the doubt made visible. The
 * screen therefore never insists on an answer — it counts what was left open
 * and says so afterwards.
 */
const TODAY = new Date().toISOString().slice(0, 10);

/** What a wine's row has been answered with, before the walk is sent. */
type Answer = {
  /** How many of this wine are standing here. Null until somebody says. */
  readonly seen: number | null;
  /** What became of the ones that are not. Null is *leave it unconfirmed*. */
  readonly outcome: StocktakeOutcome | null;
  readonly note: string;
};

const UNANSWERED: Answer = { seen: null, outcome: null, note: "" };

/** A bottle found on this shelf that the record places somewhere else. */
type FoundBottle = {
  readonly bottleId: string;
  readonly number: number;
  readonly name: string;
  /** Where the record had it, said in the words the record uses. */
  readonly wasAt: string;
};

/**
 * What the walk will send.
 *
 * The ids are taken off the front of each wine's list rather than chosen,
 * because bottles of one wine on one shelf are indistinguishable objects: a
 * person answers *two of the three are here* and it is the interface's job to
 * decide which two ids that means. The history still gets actual objects,
 * which is what lets it answer where a given bottle has been.
 */
function walkToRequest(
  shelfId: string,
  wines: readonly ShelfWine[],
  answers: ReadonlyMap<string, Answer>,
  found: readonly FoundBottle[],
): RecordStocktakeRequest {
  const confirmed: string[] = [];
  const gone: RecordStocktakeRequest["gone"] = [];

  for (const wine of wines) {
    const answer = answers.get(wine.wineId) ?? UNANSWERED;
    if (answer.seen === null) continue;
    const standing = wine.bottles.slice(0, answer.seen);
    const missing = wine.bottles.slice(answer.seen);
    for (const bottle of standing) confirmed.push(bottle.id);
    if (answer.outcome !== null) {
      for (const bottle of missing) {
        gone.push({
          bottleId: bottle.id,
          outcome: answer.outcome,
          note: answer.note.trim() === "" ? null : answer.note.trim(),
        });
      }
    }
  }

  return {
    shelfId,
    walkedOn: TODAY,
    confirmed,
    found: found.map((bottle) => bottle.bottleId),
    gone,
  };
}

type State =
  | { status: "loading" }
  | { status: "ready"; storages: StorageSummary[] }
  | { status: "failed" };

export function Stocktake() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  const mayWrite = useMayWrite();
  const [params, setParams] = useSearchParams();
  const shelfId = params.get("hylla") ?? "";

  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(() => {
    void fetchStorages(cellarId)
      .then((storages) => setState({ status: "ready", storages }))
      .catch(() => setState({ status: "failed" }));
  }, [cellarId]);

  useReload(load);

  // Counting a shelf writes what was found there, so it is not a guest's
  // errand. The link is gone from the row; the address still works.
  if (mayWrite === false) {
    return (
      <section>
        <CellarHeading cellarId={cellarId} current="stocktake" />
        <OwnersOnly cellarId={cellarId} />
      </section>
    );
  }

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="stocktake" />
      <p className="text-muted mt-2 max-w-prose text-sm">{t("stocktake.intro")}</p>

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("wines.loadFailed")}
        </p>
      )}

      {state.status === "ready" &&
        (shelfId === "" ? (
          <ShelfChoice
            storages={state.storages}
            onPick={(picked) => setParams({ hylla: picked }, { replace: false })}
          />
        ) : (
          <Walk
            key={shelfId}
            cellarId={cellarId}
            shelfId={shelfId}
            onLeave={() => setParams({}, { replace: false })}
          />
        ))}
    </section>
  );
}

/**
 * Which door to open.
 *
 * The cabinets in the order they are named, their shelves beneath them. No
 * counts and no temperatures: this is a list of places to stand, and the
 * furniture has a page of its own where it is described. Each shelf says when
 * it was last walked, because the question at this screen is which ones are
 * left.
 */
function ShelfChoice({
  storages,
  onPick,
}: {
  storages: readonly StorageSummary[];
  onPick: (shelfId: string) => void;
}) {
  const { t } = useTranslation();
  const shelved = storages.filter((storage) => storage.shelves.length > 0);

  if (shelved.length === 0) {
    return (
      <p className="text-muted mt-8 max-w-prose text-sm">{t("stocktake.noShelves")}</p>
    );
  }

  return (
    <div className="mt-8">
      <h2 className="font-display text-ink text-base">{t("stocktake.pickShelf")}</h2>
      {shelved.map((storage) => (
        <section key={storage.id} className="mt-6">
          <h3 className="text-muted text-sm">{storage.name}</h3>
          <ul className="border-line mt-2 border-t">
            {storage.shelves.map((shelf) => (
              <li key={shelf.id} className="border-line border-b">
                <button
                  type="button"
                  onClick={() => onPick(shelf.id)}
                  className="hover:bg-raised font-display text-accent flex w-full items-baseline justify-between gap-4 px-1 py-4 text-left text-lg transition-colors"
                >
                  <span>{shelf.name}</span>
                  {/* Absent means an older API that does not say, which is
                      not the same as never walked, so it prints nothing. */}
                  {shelf.lastWalkedAt !== undefined && (
                    <span className="font-sans text-muted shrink-0 text-sm">
                      {shelf.lastWalkedAt === null
                        ? t("stocktake.neverWalkedShort")
                        : shelf.lastWalkedAt}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

type WalkState =
  | { status: "loading" }
  | { status: "ready"; shelf: ShelfContentsResponse }
  | { status: "failed" };

/** How the save went. `stale` is the one failure worth its own sentence. */
type SaveState =
  | { status: "idle" }
  | { status: "saving" }
  | { status: "saved"; result: StocktakeResponse }
  | { status: "failed"; reason: "stale" | "other" };

function Walk({
  cellarId,
  shelfId,
  onLeave,
}: {
  cellarId: string;
  shelfId: string;
  onLeave: () => void;
}) {
  const { t } = useTranslation();
  const [walk, setWalk] = useState<WalkState>({ status: "loading" });
  const [answers, setAnswers] = useState<ReadonlyMap<string, Answer>>(new Map());
  const [found, setFound] = useState<readonly FoundBottle[]>([]);
  const [save, setSave] = useState<SaveState>({ status: "idle" });

  const load = useCallback(() => {
    void fetchShelfContents(cellarId, shelfId)
      .then((shelf) => setWalk({ status: "ready", shelf }))
      .catch(() => setWalk({ status: "failed" }));
  }, [cellarId, shelfId]);

  useEffect(load, [load]);

  const answer = (wineId: string, next: Answer) =>
    setAnswers((current) => new Map(current).set(wineId, next));

  const shelf = walk.status === "ready" ? walk.shelf : null;
  const wines = shelf?.wines ?? [];

  const recorded = useMemo(
    () => wines.reduce((total, wine) => total + wine.bottles.length, 0),
    [wines],
  );
  const request = useMemo(
    () => (shelf === null ? null : walkToRequest(shelfId, wines, answers, found)),
    [shelf, shelfId, wines, answers, found],
  );
  const answered = request === null ? 0 : request.confirmed.length + request.gone.length;
  const openBottles = recorded - answered;

  const send = async () => {
    if (request === null) return;
    setSave({ status: "saving" });
    try {
      const result = await recordStocktake(cellarId, request);
      setSave({ status: "saved", result });
    } catch (error) {
      // The shelf changing under a walk is its own sentence: the fix is to
      // read the shelf again, and a generic failure would send somebody
      // looking for a fault that is not there.
      const stale = error instanceof ApiError && error.status === 409;
      setSave({ status: "failed", reason: stale ? "stale" : "other" });
    }
  };

  if (walk.status === "loading") {
    return <p className="text-muted mt-6 text-sm">{t("loading")}</p>;
  }
  if (walk.status === "failed" || shelf === null) {
    return (
      <p role="alert" className="text-past mt-6 text-sm">
        {t("wines.loadFailed")}
      </p>
    );
  }

  if (save.status === "saved") {
    return (
      <div className="mt-8">
        <h2 className="font-display text-ink text-lg">{t("stocktake.saved")}</h2>
        <ul className="text-muted mt-3 max-w-prose space-y-1 text-sm">
          <li>{t("stocktake.result.confirmed", { count: save.result.confirmed })}</li>
          {save.result.moved > 0 && (
            <li>{t("stocktake.result.moved", { count: save.result.moved })}</li>
          )}
          {save.result.gone > 0 && (
            <li>{t("stocktake.result.gone", { count: save.result.gone })}</li>
          )}
          <li className={save.result.unconfirmed > 0 ? "text-ink" : undefined}>
            {save.result.unconfirmed === 0
              ? t("stocktake.result.unconfirmedNone")
              : t("stocktake.result.unconfirmed", { count: save.result.unconfirmed })}
          </li>
        </ul>
        <p className="mt-6">
          <button
            type="button"
            onClick={onLeave}
            className="text-accent underline-offset-4 hover:underline"
          >
            {t("stocktake.another")}
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="font-display text-ink text-lg">
          {shelf.storageName} · {shelf.shelfName}
        </h2>
        <button
          type="button"
          onClick={onLeave}
          className="text-accent shrink-0 text-sm underline-offset-4 hover:underline"
        >
          {t("stocktake.another")}
        </button>
      </div>
      <p className="text-muted mt-1 text-sm">
        {shelf.lastWalkedAt === null
          ? t("stocktake.neverWalked")
          : t("stocktake.lastWalked", { date: shelf.lastWalkedAt })}
      </p>

      {wines.length === 0 ? (
        <p className="text-muted mt-8 max-w-prose text-sm">{t("stocktake.emptyShelf")}</p>
      ) : (
        <ul className="border-line mt-6 border-t">
          {wines.map((wine) => (
            <WineOnShelf
              key={wine.wineId}
              wine={wine}
              answer={answers.get(wine.wineId) ?? UNANSWERED}
              onAnswer={(next) => answer(wine.wineId, next)}
            />
          ))}
        </ul>
      )}

      <FoundHere
        cellarId={cellarId}
        shelfId={shelfId}
        found={found}
        onAdd={(bottle) => setFound((current) => [...current, bottle])}
        onRemove={(bottleId) =>
          setFound((current) => current.filter((row) => row.bottleId !== bottleId))
        }
      />

      <div className="border-line mt-10 border-t pt-6">
        <p className="text-muted max-w-prose text-sm">
          {openBottles === 0
            ? t("stocktake.openNone")
            : t("stocktake.open", { count: openBottles })}
        </p>
        {save.status === "failed" && (
          <p role="alert" className="text-past mt-3 max-w-prose text-sm">
            {t(
              save.reason === "stale" ? "stocktake.staleFailed" : "stocktake.saveFailed",
            )}
          </p>
        )}
        <button
          type="button"
          onClick={() => void send()}
          disabled={save.status === "saving"}
          className={`${actionButton} mt-4`}
        >
          {t(save.status === "saving" ? "stocktake.saving" : "stocktake.save")}
        </button>
      </div>
    </div>
  );
}

const OUTCOMES: readonly StocktakeOutcome[] = [
  "Consumed",
  "Gifted",
  "Sold",
  "Lost",
  "Broken",
];

/**
 * One wine on the shelf, and the one question asked of it: how many are here.
 *
 * Answered by tapping a number rather than by a stepper, because the counts
 * are small — a shelf holds one to six of anything — and one tap is the whole
 * interaction standing in front of a cabinet with a phone in one hand. No
 * number is preselected: a row that arrived already saying *all three are
 * here* would let a walk confirm a shelf nobody looked at, which is the one
 * thing this screen must not make easy.
 *
 * The ending only appears once an answer leaves bottles unaccounted for, and
 * it opens on *leave it unconfirmed*, which is the honest default and the
 * common one.
 */
function WineOnShelf({
  wine,
  answer,
  onAnswer,
}: {
  wine: ShelfWine;
  answer: Answer;
  onAnswer: (next: Answer) => void;
}) {
  const { t } = useTranslation();
  const recorded = wine.bottles.length;
  const missing = answer.seen === null ? 0 : recorded - answer.seen;

  return (
    <li className="border-line border-b py-5">
      <div className="grid grid-cols-[2.75rem_1fr] gap-x-4">
        <div className="font-display text-muted pt-0.5 text-right text-lg">
          {wine.number}
        </div>
        <div className="min-w-0">
          <div className="flex items-baseline justify-between gap-4">
            <h3 className="font-display truncate text-lg leading-tight">{wine.name}</h3>
            <span className="font-display text-muted shrink-0 text-base">
              {wine.isNonVintage ? "NV" : (wine.vintage ?? "—")}
            </span>
          </div>
          <p className="text-muted mt-0.5 truncate text-sm">{wine.producer}</p>

          <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-2">
            <span className="text-muted text-sm">
              {t("stocktake.recorded", { count: recorded })}
            </span>
            {Array.from({ length: recorded + 1 }, (_, seen) => seen).map((seen) => (
              <button
                key={seen}
                type="button"
                aria-pressed={answer.seen === seen}
                onClick={() =>
                  onAnswer(
                    answer.seen === seen
                      ? UNANSWERED
                      : {
                          ...answer,
                          seen,
                          outcome: seen === recorded ? null : answer.outcome,
                        },
                  )
                }
                className={`font-display hover:bg-raised h-9 min-w-9 rounded-sm border text-sm transition-colors ${
                  answer.seen === seen ? narrowed : resting
                }`}
              >
                {seen}
              </button>
            ))}
          </div>

          {missing > 0 && (
            <div className="mt-3">
              <label className={fieldLabel} htmlFor={`${wine.wineId}-outcome`}>
                {t("stocktake.whatHappened", { count: missing })}
              </label>
              <div className="mt-1">
                <Select
                  id={`${wine.wineId}-outcome`}
                  value={answer.outcome ?? ""}
                  onChange={(value) =>
                    onAnswer({
                      ...answer,
                      outcome: value === "" ? null : (value as StocktakeOutcome),
                    })
                  }
                >
                  <option value="">{t("stocktake.outcome.leaveOpen")}</option>
                  {OUTCOMES.map((outcome) => (
                    <option key={outcome} value={outcome}>
                      {t(`stocktake.outcome.${outcome}`)}
                    </option>
                  ))}
                </Select>
              </div>
              {answer.outcome !== null && (
                <>
                  <label className={`${fieldLabel} mt-3`} htmlFor={`${wine.wineId}-note`}>
                    {t("stocktake.note")}
                  </label>
                  <input
                    id={`${wine.wineId}-note`}
                    className={`${control} ${resting} mt-1`}
                    value={answer.note}
                    onChange={(event) =>
                      onAnswer({ ...answer, note: event.target.value })
                    }
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * The other kind of mismatch: a bottle standing here that the record places
 * somewhere else.
 *
 * `project-scope.md` calls this one *simply a move nobody recorded*, and this
 * is where it is resolved — on the shelf the bottle is actually on, by the
 * person holding it. It is asked for by the number on the label, because that
 * number is the one thing written on the bottle in your hand.
 *
 * Only bottles the record puts elsewhere are offered: one already on this
 * shelf belongs to the list above, where it is confirmed rather than moved.
 */
function FoundHere({
  cellarId,
  shelfId,
  found,
  onAdd,
  onRemove,
}: {
  cellarId: string;
  shelfId: string;
  found: readonly FoundBottle[];
  onAdd: (bottle: FoundBottle) => void;
  onRemove: (bottleId: string) => void;
}) {
  const { t } = useTranslation();
  const [number, setNumber] = useState("");
  const [looking, setLooking] = useState(false);
  const [result, setResult] = useState<
    | { kind: "none" }
    | { kind: "unknownNumber" }
    | { kind: "allHere" }
    | { kind: "bottles"; wine: WineDetail; bottles: FoundBottle[] }
  >({ kind: "none" });

  const look = async () => {
    const wanted = Number(number);
    if (!Number.isInteger(wanted)) return;
    setLooking(true);
    try {
      const wines = await fetchWines(cellarId);
      const row = wines.find((wine) => wine.number === wanted);
      if (row === undefined) {
        setResult({ kind: "unknownNumber" });
        return;
      }
      const wine = await fetchWine(cellarId, row.id);
      // The bottle summary names its shelf rather than identifying it, so a
      // bottle already on this shelf cannot be recognised from here. It does
      // not need to be: it is in the list above, where it is confirmed rather
      // than moved, and adding it twice is refused by the route.
      const elsewhere: FoundBottle[] = [];
      for (const bottle of wine.bottles) {
        if (found.some((already) => already.bottleId === bottle.id)) continue;
        if (bottle.status === "Away") {
          elsewhere.push({
            bottleId: bottle.id,
            number: wine.number,
            name: wine.name,
            wasAt: bottle.awayNote ?? t("stocktake.away"),
          });
        } else if (bottle.status === "InCellar" && bottle.shelfName !== null) {
          elsewhere.push({
            bottleId: bottle.id,
            number: wine.number,
            name: wine.name,
            wasAt: `${bottle.storageName ?? ""} · ${bottle.shelfName}`,
          });
        }
      }
      setResult(
        elsewhere.length === 0
          ? { kind: "allHere" }
          : { kind: "bottles", wine, bottles: elsewhere },
      );
    } catch {
      setResult({ kind: "unknownNumber" });
    } finally {
      setLooking(false);
    }
  };

  return (
    <section className="mt-10">
      <h2 className="font-display text-ink text-base">{t("stocktake.found.title")}</h2>
      <p className="text-muted mt-1 max-w-prose text-sm">{t("stocktake.found.hint")}</p>

      <div className="mt-3 flex items-end gap-3">
        <div className="w-28">
          <label className={fieldLabel} htmlFor={`${shelfId}-number`}>
            {t("stocktake.found.number")}
          </label>
          <input
            id={`${shelfId}-number`}
            className={`${control} ${resting} mt-1`}
            inputMode="numeric"
            value={number}
            onChange={(event) => setNumber(event.target.value)}
          />
        </div>
        <button
          type="button"
          onClick={() => void look()}
          disabled={looking || number.trim() === ""}
          className={actionButton}
        >
          {t(looking ? "stocktake.found.looking" : "stocktake.found.look")}
        </button>
      </div>

      {result.kind === "unknownNumber" && (
        <p className="text-muted mt-3 text-sm">{t("stocktake.found.unknown")}</p>
      )}
      {result.kind === "allHere" && (
        <p className="text-muted mt-3 text-sm">{t("stocktake.found.allHere")}</p>
      )}
      {result.kind === "bottles" && (
        <ul className="border-line mt-3 border-t">
          {result.bottles.map((bottle) => (
            <li
              key={bottle.bottleId}
              className="border-line flex items-baseline justify-between gap-4 border-b py-3"
            >
              <span className="min-w-0 truncate text-sm">
                <span className="font-display">{bottle.number}</span> {bottle.name}
                <span className="text-muted"> — {bottle.wasAt}</span>
              </span>
              <button
                type="button"
                onClick={() => {
                  onAdd(bottle);
                  setResult({ kind: "none" });
                  setNumber("");
                }}
                className={actionButton}
              >
                {t("stocktake.found.add")}
              </button>
            </li>
          ))}
        </ul>
      )}

      {found.length > 0 && (
        <ul className="border-line mt-4 border-t">
          {found.map((bottle) => (
            <li
              key={bottle.bottleId}
              className="border-line flex items-baseline justify-between gap-4 border-b py-3"
            >
              <span className="min-w-0 truncate text-sm">
                <span className="font-display">{bottle.number}</span> {bottle.name}
                <span className="text-muted"> — {t("stocktake.found.moving")}</span>
              </span>
              <button
                type="button"
                onClick={() => onRemove(bottle.bottleId)}
                className="text-accent shrink-0 text-sm underline-offset-4 hover:underline"
              >
                {t("stocktake.found.remove")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
